import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Button } from "antd";
import { Brain } from "@phosphor-icons/react";
import {
  flattenFolderTree,
  findAIClaimFileFolder,
  formatFolderOptionLabel,
} from "./aiUploadDestination";

/**
 * Modal that asks the user where to drop the AI Upload before kicking off the
 * actual upload. Mounted between file-picker selection and `handleUpload`.
 *
 * Default selection precedence is computed by the caller and passed in via
 * `defaultFolderKey`; the modal doesn't recompute it on its own so unit tests
 * for the precedence logic can stay in `aiUploadDestination.test.js`.
 *
 * The destination value is `null` for matter root, or a folder key string
 * (e.g. "F501887"). That matches what `getSelectedKey()` returns elsewhere
 * in this component so the downstream `handleUpload(... uniqueId ...)` call
 * doesn't need a translation step.
 */
const AIUploadDestinationModal = ({
  open,
  fileCount,
  zipCount,
  treeData,
  defaultFolderKey,
  onConfirm,
  onCancel,
}) => {
  const folderOptions = useMemo(() => flattenFolderTree(treeData), [treeData]);
  const aiClaimFileKey = useMemo(() => findAIClaimFileFolder(treeData), [treeData]);

  // null means "matter root". The Select uses a sentinel string under the hood
  // because antd's Select doesn't reliably support `null` as a value.
  const ROOT_SENTINEL = "__matter_root__";
  const valueFromKey = (k) => (k == null ? ROOT_SENTINEL : k);
  const keyFromValue = (v) => (v === ROOT_SENTINEL ? null : v);

  const [selectedValue, setSelectedValue] = useState(valueFromKey(defaultFolderKey));

  useEffect(() => {
    // Re-sync when modal re-opens with a different default (different matter).
    if (open) setSelectedValue(valueFromKey(defaultFolderKey));
  }, [open, defaultFolderKey]);

  const options = useMemo(() => {
    const opts = [
      {
        value: ROOT_SENTINEL,
        label: "Matter root (no folder)",
      },
    ];
    for (const f of folderOptions) {
      const baseLabel = formatFolderOptionLabel(f);
      const label =
        aiClaimFileKey && f.key === aiClaimFileKey
          ? `${baseLabel}  \u2728 AI Claim File`
          : baseLabel;
      opts.push({ value: f.key, label });
    }
    return opts;
  }, [folderOptions, aiClaimFileKey]);

  const handleConfirm = () => {
    onConfirm(keyFromValue(selectedValue));
  };

  // Server-side zips are extracted after upload, so their file counts
  // aren't known yet — describe the selection honestly instead of "0 files".
  const zipLabel =
    zipCount > 0
      ? `${zipCount} zip archive${zipCount === 1 ? "" : "s"} (contents extracted after upload)`
      : null;
  const fileLabel =
    fileCount > 0
      ? `${fileCount === 1 ? "1 file" : `${fileCount} files`}${zipLabel ? ` + ${zipLabel}` : ""}`
      : zipLabel || "0 files";

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Brain size={18} color="#7C3AED" />
          <span>AI Upload</span>
        </div>
      }
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={460}
    >
      <div style={{ paddingTop: "8px" }}>
        <p style={{ margin: 0, color: "#4B5563", fontSize: "14px" }}>
          Upload {fileLabel} to:
        </p>
        <Select
          data-testid="ai-upload-destination-select"
          value={selectedValue}
          onChange={(v) => setSelectedValue(v)}
          options={options}
          style={{ width: "100%", marginTop: "10px" }}
          showSearch
          optionFilterProp="label"
        />
        {aiClaimFileKey ? (
          <p
            style={{
              marginTop: "10px",
              fontSize: "12px",
              color: "#6B7280",
            }}
          >
            Defaulting to <strong>AI Claim File</strong> &mdash; the folder
            built specifically for AI-processed claim documents.
          </p>
        ) : (
          <p
            style={{
              marginTop: "10px",
              fontSize: "12px",
              color: "#6B7280",
            }}
          >
            This matter doesn&rsquo;t have an &ldquo;AI Claim File&rdquo; folder
            yet. Pick a destination, or leave as matter root.
          </p>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "20px",
        }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          data-testid="ai-upload-destination-confirm"
        >
          Upload
        </Button>
      </div>
    </Modal>
  );
};

AIUploadDestinationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  fileCount: PropTypes.number.isRequired,
  zipCount: PropTypes.number,
  treeData: PropTypes.array,
  defaultFolderKey: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

AIUploadDestinationModal.defaultProps = {
  zipCount: 0,
  treeData: [],
  defaultFolderKey: null,
};

export default AIUploadDestinationModal;
